// ═══════════════════════════════════════════════════════════════════════
// field-renderers.tsx — shared read-mode field renderers
// ═══════════════════════════════════════════════════════════════════════
// One source of truth for how a value renders given its ScreenField type.
// Used by:
//   - DetailDrawer (vertical key/value list)
//   - DataTableRenderer (table cells)
//   - KanbanRenderer (card content)
//
// Each renderer takes { value, field? }. The field is optional and unused
// for most types today; it gives a hook point for future per-field config
// (e.g. FieldCurrency honoring field.currency, FieldDate honoring
// field.format) without API churn.
//
// renderField(field, row) is the canonical router — given a field and the
// containing row, it returns the right component with the right value.
// ═══════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import type { ScreenField } from "@sloe/schema";

// ─── Field-schema helpers (moved here from DetailDrawer so table + kanban
// can share the inferred-fields fallback) ─────────────────────────────

export const SKIP_KEYS = new Set([
  "id",
  "createdAt",
  "created_at",
  "updatedAt",
  "updated_at",
]);

export function isSkippedKey(key: string): boolean {
  return key.startsWith("_") || SKIP_KEYS.has(key);
}

export function titleCaseKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());
}

export function inferFields(row: Record<string, any>): ScreenField[] {
  return Object.keys(row)
    .filter((k) => !isSkippedKey(k))
    .map((k) => ({ key: k, label: titleCaseKey(k), type: "text" as const }));
}

// ─── Select color cycle ───────────────────────────────────────────────
// Keyword-based match against the lowercased value. First group hit
// wins; falls back to indigo. The class literals are kept in source so
// Tailwind's content scanner picks them up at build time.

// DESIGN-1 — expanded vocabulary covering common project/CRM/deal statuses.
// Substring-matched (case-insensitive) — "In Progress", "in_progress", and
// "in-progress" all hit the same group. First group wins; falls back to
// indigo. Class literals are kept in source so Tailwind's scanner picks
// them up at build time.
const COLOR_GROUPS: Array<{ keywords: string[]; classes: string }> = [
  // Green — positive / shipped
  { keywords: ["new", "active", "approved", "complete", "won", "delivered", "shipped"],
    classes: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  // Amber — in-flight / mid-funnel
  { keywords: ["in progress", "pending", "review", "medium", "proposal", "negotiation"],
    classes: "bg-amber-50 text-amber-700 border-amber-200" },
  // Red — urgent / stuck
  { keywords: ["high", "blocked", "urgent", "critical"],
    classes: "bg-rose-50 text-rose-700 border-rose-200" },
  // Blue — informational / closed-archival
  { keywords: ["closed", "low"],
    classes: "bg-blue-50 text-blue-700 border-blue-200" },
];

const SELECT_FALLBACK = "bg-indigo-50 text-indigo-700 border-indigo-200";

export function selectColorClasses(value: any): string {
  if (value === null || value === undefined || value === "") return SELECT_FALLBACK;
  const s = String(value).toLowerCase();
  for (const group of COLOR_GROUPS) {
    if (group.keywords.some((k) => s.includes(k))) return group.classes;
  }
  return SELECT_FALLBACK;
}

// ─── Per-type renderers ───────────────────────────────────────────────

interface RendererProps {
  value: any;
  field?: ScreenField;
}

export function FieldText({ value }: RendererProps) {
  const display = value === null || value === undefined || value === ""
    ? "—"
    : String(value);
  return <span className="text-sm text-slate-900 whitespace-pre-wrap break-words">{display}</span>;
}

export function FieldCurrency({ value }: RendererProps) {
  if (typeof value === "number" && Number.isFinite(value)) {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
    return <span className="text-sm font-semibold text-slate-900 tabular-nums">{formatted}</span>;
  }
  // Already-formatted strings (e.g. "$48,500") pass through. Numeric-looking
  // strings could be parsed, but coercion is risky — leave to upstream data
  // cleanup if needed.
  if (value === null || value === undefined || value === "") {
    return <span className="text-sm text-slate-500">—</span>;
  }
  return <span className="text-sm font-semibold text-slate-900 tabular-nums">{String(value)}</span>;
}

// DESIGN-1 — Relative date formatting for dates within ±30 days of now.
// "Today" / "Tomorrow" / "Yesterday" / "In N days" / "N days ago". Outside
// the window we fall back to the absolute Intl date so far-future quarters
// (Q3 close dates) still read clearly.
function formatRelativeDate(d: Date): string {
  const now = new Date();
  // Compare on midnight boundaries so "today" doesn't flip at noon.
  const dMid = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const nMid = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const diffDays = Math.round((dMid - nMid) / (1000 * 60 * 60 * 24));
  if (Math.abs(diffDays) > 30) {
    return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" }).format(d);
  }
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > 0) return `In ${diffDays} days`;
  return `${Math.abs(diffDays)} days ago`;
}

export function FieldDate({ value }: RendererProps) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-sm text-slate-500">—</span>;
  }
  const looksDate =
    value instanceof Date ||
    (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value));
  if (looksDate) {
    const d = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(d.getTime())) {
      const formatted = formatRelativeDate(d);
      return <span className="text-sm text-slate-700 tabular-nums">{formatted}</span>;
    }
  }
  // Display-only strings ("May 8") pass through.
  return <span className="text-sm text-slate-700">{String(value)}</span>;
}

export function FieldSelect({ value }: RendererProps) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-sm text-slate-500">—</span>;
  }
  const classes = selectColorClasses(value);
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${classes}`}>
      {String(value)}
    </span>
  );
}

export function FieldUser({ value }: RendererProps) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-sm text-slate-500">—</span>;
  }
  // Tolerate both shapes: a plain string (current state) or an object with
  // a name/avatar_url (future). We don't assume the object shape — just
  // probe for the keys we care about and fall through to String() if it's
  // something else.
  let name: string;
  let avatarUrl: string | undefined;
  if (value && typeof value === "object") {
    name = String((value as any).name ?? (value as any).email ?? "");
    avatarUrl = (value as any).avatar_url ? String((value as any).avatar_url) : undefined;
  } else {
    name = String(value);
  }
  const initial = (name.match(/[A-Za-z]/)?.[0] || "?").toUpperCase();
  return (
    <span className="inline-flex items-center gap-2">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt=""
          width={24}
          height={24}
          className="w-6 h-6 rounded-full object-cover shrink-0"
        />
      ) : (
        <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600 shrink-0">
          {initial}
        </span>
      )}
      <span className="text-sm text-slate-900 truncate">{name}</span>
    </span>
  );
}

// ─── Router ───────────────────────────────────────────────────────────

export function renderField(field: ScreenField, row: Record<string, any>) {
  const value = row[field.key];
  switch (field.type) {
    case "currency": return <FieldCurrency value={value} field={field} />;
    case "date":     return <FieldDate value={value} field={field} />;
    case "select":   return <FieldSelect value={value} field={field} />;
    case "user":     return <FieldUser value={value} field={field} />;
    case "text":
    default:         return <FieldText value={value} field={field} />;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// EDITORS — read/write counterparts to the renderers above
// ═══════════════════════════════════════════════════════════════════════
// Each editor:
//   - Auto-focuses on mount
//   - Calls onSave(newValue) on Enter (or change, for select) or on blur
//   - Calls onCancel() on Esc
//   - Normalizes empty input to null per spec
// FieldUser intentionally has no editor — user-picker UI is a later piece.
// ═══════════════════════════════════════════════════════════════════════

interface EditorProps {
  value: any;
  field: ScreenField;
  /** Called with the new value once the user commits (Enter / blur / change). */
  onSave: (newValue: any) => void;
  /** Called when the user presses Esc to abandon the edit. */
  onCancel: () => void;
  /**
   * For FieldSelectEditor only: distinct values pulled from other rows in
   * this screen, used as a fallback option list when ScreenField.options
   * is empty. Always includes the current value if present.
   */
  optionFallback?: string[];
}

const inputBase =
  "w-full px-3 py-2 text-sm bg-white border border-indigo-300 rounded-md " +
  "focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500";

function emptyToNull(v: any): any {
  if (v === "" || v === undefined) return null;
  return v;
}

export function FieldTextEditor({ value, onSave, onCancel }: EditorProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<string>(value == null ? "" : String(value));
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const commit = () => onSave(emptyToNull(draft.trim()));
  return (
    <input
      ref={ref}
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
      onBlur={commit}
      className={inputBase}
    />
  );
}

export function FieldCurrencyEditor({ value, onSave, onCancel }: EditorProps) {
  const ref = useRef<HTMLInputElement>(null);
  // Initial value: number passes through; string "$48,500" → "48500"; null → "".
  const initial =
    typeof value === "number" ? String(value)
    : typeof value === "string" ? value.replace(/[^0-9.\-]/g, "")
    : "";
  const [draft, setDraft] = useState<string>(initial);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const commit = () => {
    if (draft.trim() === "") { onSave(null); return; }
    const n = Number(draft);
    onSave(Number.isFinite(n) ? n : null);
  };
  return (
    <input
      ref={ref}
      type="number"
      inputMode="decimal"
      step="any"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
      onBlur={commit}
      className={`${inputBase} text-right tabular-nums`}
      placeholder="0"
    />
  );
}

export function FieldDateEditor({ value, onSave, onCancel }: EditorProps) {
  const ref = useRef<HTMLInputElement>(null);
  // Native date input wants yyyy-mm-dd. Try to coerce; show empty if unparsable.
  const initial = (() => {
    if (!value) return "";
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    if (typeof value === "string") {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    return "";
  })();
  const [draft, setDraft] = useState<string>(initial);
  useEffect(() => { ref.current?.focus(); }, []);
  const commit = () => onSave(emptyToNull(draft));
  return (
    <input
      ref={ref}
      type="date"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
      onBlur={commit}
      className={inputBase}
    />
  );
}

export function FieldSelectEditor({ value, field, onSave, onCancel, optionFallback }: EditorProps) {
  const ref = useRef<HTMLSelectElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);

  // Build option list: schema-defined options first, else distinct values
  // from the same field on other rows. Always include current value so the
  // editor can represent the existing state even if it isn't in options yet.
  const baseOptions = (field.options && field.options.length > 0)
    ? field.options
    : (optionFallback || []);
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const o of baseOptions) {
    if (o == null) continue;
    const s = String(o);
    if (seen.has(s)) continue;
    seen.add(s);
    ordered.push(s);
  }
  if (value != null && value !== "" && !seen.has(String(value))) {
    ordered.unshift(String(value));
    seen.add(String(value));
  }

  return (
    <select
      ref={ref}
      value={value == null ? "" : String(value)}
      onChange={(e) => onSave(emptyToNull(e.target.value))}
      onKeyDown={(e) => {
        if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
      onBlur={onCancel}
      className={inputBase}
    >
      <option value="">—</option>
      {ordered.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

/**
 * Router. Returns null for user fields (no editor) so the caller can decide
 * whether to fall back to the read renderer with a small read-only note.
 */
export function renderFieldEditor(
  field: ScreenField,
  value: any,
  onSave: (newValue: any) => void,
  onCancel: () => void,
  optionFallback?: string[],
) {
  switch (field.type) {
    case "currency": return <FieldCurrencyEditor value={value} field={field} onSave={onSave} onCancel={onCancel} />;
    case "date":     return <FieldDateEditor value={value} field={field} onSave={onSave} onCancel={onCancel} />;
    case "select":   return <FieldSelectEditor value={value} field={field} onSave={onSave} onCancel={onCancel} optionFallback={optionFallback} />;
    case "user":     return null;
    case "text":
    default:         return <FieldTextEditor value={value} field={field} onSave={onSave} onCancel={onCancel} />;
  }
}
